import { z } from 'zod';
import { ORGANIZATION_UNIT_TYPES } from '../models/organization.model';

export const organizationUnitTypeSchema = z.enum(ORGANIZATION_UNIT_TYPES);

// ─── Organization (tenant) ──────────────────────────────────────────────────
// `ownerPersonIds`: persons made admin/owner of the org — assigned to an
// "Owner" position on the root OE.
export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(255),
  ownerPersonIds: z.array(z.uuid()).optional(),
});
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;

export const updateOrganizationSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(255),
  ownerPersonIds: z.array(z.uuid()).optional(),
});
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

// ─── Organization unit (OE) ─────────────────────────────────────────────────
/**
 * The holiday year a unit measures entitlement in. Empty inherits.
 *
 * A closed list rather than free text: the calculation branches on it, and a
 * typo would fall through to whatever the branch treats as "the other one".
 */
export const vacationYearTypeSchema = z.enum(['WORK_YEAR', 'CALENDAR_YEAR']);

/**
 * Months before the first entitlement is credited. `0` is "from the first
 * day", so the lower bound is zero and not one — and a whole number, because
 * the customer's field list types it as an integer.
 */
const waitingPeriodMonths = z.int().min(0).max(120);

export const createOrganizationUnitSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    type: organizationUnitTypeSchema.default('department'),
    organizationId: z.uuid(),
    // Every OE hangs on another OE. Root creation is handled by the seed,
    // which does not come through here.
    parentId: z.uuid(),
    description: z.string().trim().max(1000).optional(),
    legalName: z.string().trim().max(255).optional(),
    countryCode: z
      .string()
      .trim()
      .length(2)
      .regex(/^[A-Z]{2}$/)
      .optional(),
    /** IANA zone name; empty inherits from the parent. */
    timezone: z.string().trim().max(64).optional(),
    /** Holiday entitlement; both inherit from the parent when left out. */
    vacationWaitingPeriodMonths: waitingPeriodMonths.optional(),
    vacationYearType: vacationYearTypeSchema.optional(),
    isDeletionLocked: z.boolean().optional(),
  });
export type CreateOrganizationUnitInput = z.infer<
  typeof createOrganizationUnitSchema
>;

export const updateOrganizationUnitSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(255).optional(),
  type: organizationUnitTypeSchema.optional(),
  parentId: z.uuid().optional(),
  description: z
    .union([z.string().trim().max(1000), z.literal(''), z.null()])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value === '' || value === null
          ? null
          : value,
    ),
  legalName: z.string().trim().max(255).nullable().optional(),
  countryCode: z
    .string()
    .trim()
    .length(2)
    .regex(/^[A-Z]{2}$/)
    .nullable()
    .optional(),
  timezone: z
    .union([z.string().trim().max(64), z.literal(''), z.null()])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value === '' || value === null
          ? null
          : value,
    ),
  /**
   * Both clearable, which is how a unit is handed back to its parent's value.
   * `''` arrives from a cleared form field and means the same as null.
   */
  vacationWaitingPeriodMonths: z
    .union([waitingPeriodMonths, z.literal(''), z.null()])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value === '' || value === null
          ? null
          : value,
    ),
  vacationYearType: z
    .union([vacationYearTypeSchema, z.literal(''), z.null()])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value === '' || value === null
          ? null
          : value,
    ),
  isDeletionLocked: z.boolean().optional(),
});
export type UpdateOrganizationUnitInput = z.infer<
  typeof updateOrganizationUnitSchema
>;

// ─── Create a person inside an organization (admin/owner) ───────────────────
export const createOrganizationPersonSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.email().max(255),
  type: z.enum(['employee', 'contractor']).default('employee'),
  /** Also make this person an admin/owner of the organization. */
  isOwner: z.boolean().optional(),
});
export type CreateOrganizationPersonInput = z.infer<
  typeof createOrganizationPersonSchema
>;

// ─── Position (Stelle) ──────────────────────────────────────────────────────
export const createPositionSchema = z.object({
  name: z.string().trim().min(1).max(255),
  organizationUnitId: z.uuid(),
  description: z.string().trim().max(1000).optional(),
});
export type CreatePositionInput = z.infer<typeof createPositionSchema>;

export const updatePositionSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(255).optional(),
  organizationUnitId: z.uuid().optional(),
  description: z.string().trim().max(1000).nullable().optional(),
});
export type UpdatePositionInput = z.infer<typeof updatePositionSchema>;

export const setPositionRoleSchema = z.object({
  positionId: z.uuid(),
  roleId: z.uuid(),
});
export type SetPositionRoleInput = z.infer<typeof setPositionRoleSchema>;

// ─── Station ────────────────────────────────────────────────────────────────
export const createStationSchema = z.object({
  name: z.string().trim().min(1).max(255),
  organizationUnitId: z.uuid(),
  description: z.string().trim().max(1000).optional(),
  responsiblePersonId: z.uuid().optional(),
});
export type CreateStationInput = z.infer<typeof createStationSchema>;

export const updateStationSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(255).optional(),
  organizationUnitId: z.uuid().optional(),
  description: z
    .union([z.string().trim().max(1000), z.literal(''), z.null()])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value === '' || value === null
          ? null
          : value,
    ),
  /** Clearable: a station may be handed back without somebody responsible. */
  responsiblePersonId: z
    .union([z.uuid(), z.literal(''), z.null()])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : value === '' || value === null
          ? null
          : value,
    ),
});
export type UpdateStationInput = z.infer<typeof updateStationSchema>;

// ─── PersonPosition (Stellenbesetzung) ──────────────────────────────────────
export const assignPersonPositionSchema = z.object({
  personId: z.uuid(),
  positionId: z.uuid(),
  isPrimary: z.boolean().optional(),
});
export type AssignPersonPositionInput = z.infer<
  typeof assignPersonPositionSchema
>;

/** Drag-and-drop: move an existing assignment to another position. */
export const movePersonPositionSchema = z.object({
  toPositionId: z.uuid(),
  isPrimary: z.boolean().optional(),
});
export type MovePersonPositionInput = z.infer<typeof movePersonPositionSchema>;
